import { type QueryClient } from '@tanstack/react-query';
import { redirect } from 'react-router';

import { composites } from '../../../../common/constants/access';
import { buildTeamsOptions } from '../../../components/pages/wallet/walletHelper';
import { manager, overviewConstants, teamAdmin } from '../../../constants/appConstants';
import { getTeamUsersQuery, selectedTeamIdQuery, teamQuery, teamsQuery, type User } from '../../../queries/teamQuery';
import { getVerifyUserAccessQuery, userQuery } from '../../../queries/userQuery';
import type { PaginatedWalletListResponseSchemaType } from '../../../repositories/walletRepository';
import { isCompanyOrSupportAdmin } from '../../../selectors/userSelectors';

type WalletRouteParams = {
	teamId: string;
	userId: string;
};

let teams: { id: string; label: string }[] = [];
let navigateTo = '/wallet';

export const walletLoader = async ({
	queryClient,
	request,
	params,
}: {
	queryClient: QueryClient;
	request: Request;
	params: WalletRouteParams;
}) => {
	const verifyUserAccessQuery = getVerifyUserAccessQuery((composites as any).WRITE_COMPANY);
	const teamId = await queryClient.ensureQueryData(selectedTeamIdQuery('selectedTeamID'));
	const userSelectedTeamId = teamId?.[0] || '';
	const userData = await queryClient.ensureQueryData(userQuery);

	const userRole = isCompanyOrSupportAdmin(userData.roles)
		? overviewConstants.COMPANY_ADMIN
		: userData.roles.find(role => role.securityGroupId === userSelectedTeamId)?.roleId || '';

	let team;
	try {
		team = await queryClient.ensureQueryData(teamQuery({ teamId: teamId?.[0] || '' }));
	} catch (error) {
		team = { data: null, error, isError: true };
	}

	const hasAccess = await queryClient.ensureQueryData({
		queryKey: verifyUserAccessQuery.queryKey,
		queryFn: verifyUserAccessQuery.queryFn,
	});

	const paginatedWalletData: PaginatedWalletListResponseSchemaType = {
		count: 0,
		tasks: [],

		// we need this, so we do not disable the next pager when we have null values in tasks
		initialPaginatedWalletDataCount: 0,
		hasNextPage: false,
	};

	let paginatedFilterApiQuery: { owner: string; teamId: string; status: string };
	const paginatedFilterApiQueryDefaultQueries = {
		status: 'ACTIVE' as const,
		sortBy: 'CREATION_DATE' as const,
		sortOrder: 'DESC' as const,
	};
	switch (userRole) {
		case overviewConstants.COMPANY_ADMIN: {
			//company admin can view the wallet cards of a specific user from his team
			// or all company wallet cards
			navigateTo = request.url.includes('wallet/team') && params.teamId && params.userId ? request.url : '/wallet';
			paginatedFilterApiQuery = {
				teamId: params.teamId || '',
				owner: params.userId || '',
				...paginatedFilterApiQueryDefaultQueries,
			};

			break;
		}
		case teamAdmin:
		case manager: {
			paginatedFilterApiQuery = request.url.includes('wallet/team')
				? {
						teamId: params.teamId || teamId?.[0] || '',
						owner: params.userId || '',
						...paginatedFilterApiQueryDefaultQueries,
					}
				: {
						teamId: params.teamId || '',
						owner: params.userId || userData.userId,
						...paginatedFilterApiQueryDefaultQueries,
					};
			if (userRole === teamAdmin) {
				// team admin can view his cards or all team cards
				const isTeamWalletRoute = request.url.includes('wallet/team');
				const hasTeamAndUserParams = Boolean(params.teamId && params.userId);

				if (!isTeamWalletRoute) {
					navigateTo = '/wallet/my-cards';
				} else if (hasTeamAndUserParams) {
					navigateTo = request.url;
				} else {
					navigateTo = '/wallet/team';
				}
			} else {
				// a manager can view team cards and my wallet cards
				// and also an user cards but only for the cards where he is an approver
				navigateTo =
					request.url.includes('wallet/team') && params.teamId && params.userId ? request.url : '/wallet/my-cards';
			}
			break;
		}
		default: {
			// other role shoul view only his cards(except tradeshift suport admin who can view all cards)
			navigateTo = '/wallet/my-cards';
			paginatedFilterApiQuery = {
				teamId: params.teamId || '',
				owner: params.userId || userData.userId,
				...paginatedFilterApiQueryDefaultQueries,
			};
		}
	}

	// redirect to the correct path if the current URL does not match
	// based on user roles
	if (!request.url.endsWith(navigateTo)) {
		throw redirect(navigateTo);
	}

	const teamData = await queryClient.ensureQueryData(teamsQuery({ state: 'active' }));
	let allMembersFromAllTeams: User[] = [];
	if (userRole === overviewConstants.COMPANY_ADMIN) {
		teams = buildTeamsOptions({ teams: teamData });
		const allMembersCollections = await Promise.all(
			teamData.map(async team => {
				try {
					const teamMembers = await queryClient.ensureQueryData(getTeamUsersQuery({ teamId: team.id, query: '' }));
					return teamMembers?.users || [];
				} catch {
					return [];
				}
			}),
		);
		const uniqueMembersById = new Map<string, User>();
		for (const member of allMembersCollections.flat()) {
			if (member?.userId && !uniqueMembersById.has(member.userId)) {
				uniqueMembersById.set(member.userId, member);
			}
		}
		allMembersFromAllTeams = [...uniqueMembersById.values()];
	}

	return {
		teamId,
		userRole,
		userData,
		team,
		hasAccess,
		navigateTo,
		teams,
		paginatedWalletData,
		paginatedFilterApiQuery,
		allMembersFromAllTeams,
	};
};
